import { SxProps, Theme } from "@mui/material";
import signRequestsTokens, {
  signRequestsResponsiveTokens,
  signRequestsStatusTokens,
  SignRequestsStatusKey,
  PHONE_SM_QUERY,
} from "./SignRequests.tokens";
import signRequestsFiltersTokens, {
  TABLET_WIDE_QUERY,
} from "./SignRequestsFilters.tokens";

const {
  page,
  header,
  secondaryButton,
  primaryButton,
  breadcrumb,
  toolbar,
  tabs,
  list,
  row,
  avatars,
  status,
  kebab,
  empty,
  freshState,
} = signRequestsTokens;

const { phone, tablet } = signRequestsResponsiveTokens;
const { table } = signRequestsFiltersTokens;

/**
 * Hooks the folder row's hover rule onto its trailing chevron / overflow
 * trigger without either element needing to know about the other.
 */
export const TRAILING_CHEVRON_CLASS = "SignRequestsRow-chevron";
export const TRAILING_ACTION_CLASS = "SignRequestsRow-action";
/** The request row's tag trigger, revealed while the row is hovered. */
export const TAG_ACTION_CLASS = "SignRequestsRow-tagAction";

/** MUI's own `down("sm")`, as a string, for rules that need a nested selector. */
const PHONE_QUERY = "@media (max-width:599.95px)";

/** Shared chrome for the search field and the date-range trigger beside it. */
const toolbarField = {
  display: "flex",
  alignItems: "center",
  bgcolor: toolbar.bg,
  border: `1px solid ${toolbar.border}`,
  borderRadius: toolbar.radius,
  boxSizing: "border-box",
} as const;

/**
 * Column tracks shared by the header row, folder rows and request rows so all
 * three line up. The columns themselves — and which of them a given width has
 * room for — come from the table tokens of `Sign Requests - Filters & Tags`.
 *
 * `withTags` is false for a list read from the folder endpoint, which carries
 * no tags: the Tags track is dropped rather than drawn as a column of dashes.
 */
export const tableColumns = (withTags: boolean) =>
  withTags
    ? {
        gridTemplateColumns: {
          sm: table.gridNarrow,
          lg: table.gridLaptop,
          xl: table.gridDesktop,
        },
        [TABLET_WIDE_QUERY]: { gridTemplateColumns: table.gridTablet },
      }
    : {
        gridTemplateColumns: {
          sm: table.gridNarrow,
          lg: table.gridLaptopNoTags,
          xl: table.gridDesktopNoTags,
        },
      };

/** Chrome every row shares: its own card on a phone, a ruled line above it. */
const rowChrome = {
  alignItems: "center",
  bgcolor: { xs: list.bg, sm: "transparent" },
  border: { xs: `1px solid ${list.border}`, sm: "none" },
  borderBottom: {
    xs: `1px solid ${list.border}`,
    sm: `1px solid ${row.divider}`,
  },
  "&:last-of-type": { borderBottom: { sm: "none" } },
} as const;

const signRequestsStyles = {
  /**
   * Below `md` the reference lays the page out edge to edge with its own
   * gutters (14/16px on the phone, 26px on the tablet), so the shared
   * `CenteredAuthenticatedLayout` gutter and top margin are pulled back off
   * with negative margins first and the reference values applied on top. It
   * also spaces the blocks with one column gap instead of the per-block bottom
   * margins the desktop screen uses. From `md` up nothing changes.
   */
  page: {
    display: "flex",
    flexDirection: "column",
    gap: { xs: phone.gap.std, sm: tablet.gap, md: 0 },
    mt: { xs: "-16px", sm: "-32px", md: 0 },
    mx: { xs: "-16px", sm: "-24px", md: 0 },
    pt: {
      xs: phone.paddingTop.std,
      sm: tablet.paddingTop,
      md: page.paddingTop,
    },
    px: { xs: phone.paddingX.std, sm: tablet.paddingX, md: page.paddingX },
    pb: {
      xs: phone.paddingBottom.std,
      sm: tablet.paddingBottom,
      md: page.paddingBottom,
    },
    [PHONE_SM_QUERY]: {
      gap: phone.gap.sm,
      paddingTop: phone.paddingTop.sm,
      paddingLeft: phone.paddingX.sm,
      paddingRight: phone.paddingX.sm,
      paddingBottom: phone.paddingBottom.sm,
    },
  } as SxProps<Theme>,

  header: {
    display: "flex",
    alignItems: { xs: "flex-start", sm: "flex-end" },
    justifyContent: "space-between",
    flexDirection: "row",
    gap: { xs: 0, sm: tablet.actionsGap, md: header.gap },
    mb: { xs: 0, sm: 0, md: header.marginBottom },
  } as SxProps<Theme>,

  /** Title + strapline column; must be shrinkable beside the header actions. */
  headerText: {
    minWidth: 0,
  } as SxProps<Theme>,

  /** The phone reference carries the screen name in the app bar, not here. */
  headerTitle: {
    display: { xs: "none", sm: "block" },
    m: 0,
    color: header.titleColor,
    fontSize: { sm: tablet.titleFontSize, md: header.titleFontSize },
    fontWeight: header.titleFontWeight,
    letterSpacing: header.titleLetterSpacing,
    lineHeight: 1.2,
  } as SxProps<Theme>,

  headerSubtitle: {
    mt: { xs: 0, sm: header.subtitleMarginTop },
    color: header.subtitleColor,
    fontSize: {
      xs: phone.subtitleFontSize.std,
      sm: tablet.subtitleFontSize,
      md: header.subtitleFontSize,
    },
    lineHeight: { xs: phone.subtitleLineHeight, sm: 1.4 },
    [PHONE_SM_QUERY]: { fontSize: phone.subtitleFontSize.sm },
  } as SxProps<Theme>,

  /** Neither phone column carries the header actions. */
  headerActions: {
    display: { xs: "none", sm: "flex" },
    alignItems: "center",
    gap: { sm: tablet.actionsGap, md: header.gap },
    flexShrink: 0,
  } as SxProps<Theme>,

  /**
   * "Create Folder". The tablet reference drops the label and draws it as a
   * 44px square beside the split button; the desktop keeps the full button.
   */
  secondaryButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: secondaryButton.gap,
    minWidth: { sm: tablet.secondaryButton.size, md: 0 },
    width: { sm: tablet.secondaryButton.size, md: "auto" },
    height: { sm: tablet.secondaryButton.size, md: "auto" },
    p: { sm: 0, md: secondaryButton.padding },
    bgcolor: secondaryButton.bg,
    border: `1px solid ${secondaryButton.border}`,
    borderRadius: {
      sm: tablet.secondaryButton.radius,
      md: secondaryButton.radius,
    },
    color: secondaryButton.color,
    fontSize: secondaryButton.fontSize,
    fontWeight: secondaryButton.fontWeight,
    lineHeight: "normal",
    textTransform: "none",
    whiteSpace: "nowrap",
    flexShrink: 0,
    "&:hover": {
      bgcolor: secondaryButton.bg,
      borderColor: secondaryButton.hoverBorder,
      color: secondaryButton.hoverColor,
    },
  } as SxProps<Theme>,

  /** Dropped with the button's square form on the tablet. */
  secondaryButtonLabel: {
    display: { xs: "none", md: "inline" },
  } as SxProps<Theme>,

  secondaryButtonGlyph: {
    width: {
      sm: tablet.secondaryButton.iconSize,
      md: secondaryButton.iconSize,
    },
    height: {
      sm: tablet.secondaryButton.iconSize,
      md: secondaryButton.iconSize,
    },
  } as SxProps<Theme>,

  /**
   * Phone stand-in for the header's split button: the 390 column of the
   * reference runs "Create Request" full width under the strapline. The 375
   * column has no room for it and gets the floating pill below instead.
   */
  createMobile: {
    display: { xs: "flex", sm: "none" },
    width: "100%",
    "& > .MuiBox-root": { width: "100%" },
    "& .MuiButtonGroup-root": { width: "100%" },
    "& .MuiButtonGroup-root > .MuiButton-root:first-of-type": {
      flex: 1,
      justifyContent: "center",
      p: phone.createButton.padding,
      fontSize: phone.createButton.fontSize,
      gap: phone.createButton.gap,
      borderRadius: `${phone.createButton.radius} 0 0 ${phone.createButton.radius}`,
    },
    "& .MuiButtonGroup-root > .MuiButton-root:last-of-type": {
      width: phone.createButton.caretWidth,
      minWidth: phone.createButton.caretWidth,
      borderRadius: `0 ${phone.createButton.radius} ${phone.createButton.radius} 0`,
    },
    // The reference drops the request-type panel to the button's own width
    // rather than the shared control's fixed 220px card.
    "& .MuiCollapse-root .MuiPaper-root": {
      width: "auto",
      left: 0,
      right: 0,
    },
  } as SxProps<Theme>,

  /** Neither phone column carries the trail; the app bar takes it instead. */
  breadcrumb: {
    display: { xs: "none", sm: "flex" },
    alignItems: "center",
    flexWrap: "wrap",
    gap: breadcrumb.gap,
    mb: { sm: 0, md: breadcrumb.marginBottom },
  } as SxProps<Theme>,

  breadcrumbItem: (isLast: boolean, disabled: boolean) =>
    ({
      display: "inline-flex",
      alignItems: "center",
      gap: "7px",
      p: 0,
      /*
        A crumb is a `Button`, and the theme gives every button a 40px height
        (see `button.height`). Zeroing the padding is not enough — without this
        the trail reads as a 40px band around one line of 14px text, which is
        what made the gap above the search row look so deep.
      */
      height: "auto",
      minHeight: 0,
      minWidth: 0,
      border: "none",
      background: "none",
      fontSize: { sm: tablet.breadcrumb.fontSize, md: breadcrumb.fontSize },
      fontWeight: breadcrumb.fontWeight,
      lineHeight: "normal",
      textTransform: "none",
      color: isLast ? breadcrumb.activeColor : breadcrumb.linkColor,
      cursor: disabled ? "not-allowed" : isLast ? "default" : "pointer",
      "&:hover": { background: "none", textDecoration: "none" },
    }) as SxProps<Theme>,

  breadcrumbSeparator: {
    width: breadcrumb.separatorSize,
    height: breadcrumb.separatorSize,
    color: breadcrumb.separatorColor,
    flexShrink: 0,
  } as SxProps<Theme>,

  breadcrumbGlyph: {
    width: { sm: tablet.breadcrumb.iconSize, md: breadcrumb.iconSize },
    height: { sm: tablet.breadcrumb.iconSize, md: breadcrumb.iconSize },
  } as SxProps<Theme>,

  /**
   * The phone reference stretches the date control to the search field's
   * height rather than centring it, so the two read as one row.
   */
  toolbar: {
    display: "flex",
    // The phone and tablet references stretch the date control to the search
    // field's height rather than centring it, so the two read as one row; the
    // picker's own input is 2px taller than the search box on its own.
    alignItems: { xs: "stretch", md: "center" },
    flexDirection: "row",
    gap: { xs: phone.search.gap, sm: tablet.toolbar.gap, md: toolbar.gap },
    mb: { xs: 0, sm: 0, md: toolbar.marginBottom },
  } as SxProps<Theme>,

  searchField: {
    ...toolbarField,
    flex: 1,
    width: "100%",
    minWidth: 0,
    p: {
      xs: phone.search.padding,
      sm: tablet.toolbar.padding,
      md: toolbar.padding,
    },
    gap: {
      xs: phone.search.gap,
      sm: tablet.toolbar.searchGap,
      md: toolbar.searchGap,
    },
    "&:focus-within": { borderColor: toolbar.focusBorder },
  } as SxProps<Theme>,

  searchGlyph: {
    width: {
      xs: phone.search.iconSize,
      sm: tablet.toolbar.searchIconSize,
      md: toolbar.searchIconSize,
    },
    height: {
      xs: phone.search.iconSize,
      sm: tablet.toolbar.searchIconSize,
      md: toolbar.searchIconSize,
    },
    color: toolbar.searchIconColor,
    flexShrink: 0,
  } as SxProps<Theme>,

  searchInput: {
    flex: 1,
    minWidth: 0,
    "& .MuiInputBase-input": {
      p: 0,
      height: "auto",
      fontSize: {
        xs: phone.search.inputFontSize,
        sm: tablet.toolbar.inputFontSize,
        md: toolbar.inputFontSize,
      },
      fontWeight: 500,
      lineHeight: "normal",
      color: toolbar.inputColor,
      "&::placeholder": {
        color: toolbar.placeholderColor,
        opacity: 1,
        fontWeight: 500,
      },
    },
  } as SxProps<Theme>,

  /**
   * 390 keeps a compact "All time" chip; 375 collapses the same control to a
   * 46px icon-only square (its value is hidden by `datePickerTrigger`).
   */
  dateField: {
    ...toolbarField,
    justifyContent: "center",
    p: {
      xs: phone.dateChip.padding,
      sm: tablet.toolbar.padding,
      md: toolbar.padding,
    },
    gap: {
      xs: phone.dateChip.gap,
      sm: tablet.toolbar.dateGap,
      md: toolbar.dateGap,
    },
    flexShrink: 0,
    fontSize: {
      xs: phone.dateChip.fontSize,
      sm: tablet.toolbar.dateFontSize,
      md: toolbar.dateFontSize,
    },
    fontWeight: toolbar.dateFontWeight,
    color: toolbar.dateColor,
    lineHeight: "normal",
    textTransform: "none",
    whiteSpace: "nowrap",
    cursor: "pointer",
    "&:hover": {
      bgcolor: toolbar.bg,
      borderColor: toolbar.hoverBorder,
    },
    // The shared picker's own wrapper stretches; keep the chip content-sized.
    "& > .MuiBox-root": { width: "auto", minWidth: 0 },
    [PHONE_SM_QUERY]: {
      width: phone.dateChip.squareWidth,
      padding: 0,
    },
  } as SxProps<Theme>,

  /**
   * Re-skins `CustomDateRangePicker`'s trigger into the design's compact
   * "All time ▾" chip — the shared control defaults to a 380px field, and an
   * `<input>` at `width: auto` still reserves ~20 characters. `fieldSizing`
   * collapses it onto its own text; the picker's `autoSizeInput` prop carries
   * browsers that lack it. The 375 column shows no value at all, so the input
   * is dropped there and the square is sized by its glyph alone.
   */
  datePickerTrigger: {
    minWidth: "0 !important",
    width: "auto",
    "& .MuiOutlinedInput-root": { p: 0, width: "auto" },
    "& .MuiInputBase-input, & .MuiOutlinedInput-input": {
      width: "auto",
      // Below `md` the chip has to hug "All time" exactly or it eats the search
      // field's width; `autoSizeInput`'s `size` attribute alone still reserves
      // a proportional-font average per character. Left at the CSS initial on
      // desktop so that column is untouched.
      fieldSizing: { xs: "content", md: "fixed" },
      p: 0,
      fontSize: {
        xs: phone.dateChip.fontSize,
        sm: tablet.toolbar.dateFontSize,
        md: toolbar.dateFontSize,
      },
      fontWeight: toolbar.dateFontWeight,
      color: toolbar.dateColor,
      lineHeight: "normal",
      textOverflow: "ellipsis",
      [PHONE_SM_QUERY]: { display: "none" },
    },
  } as SxProps<Theme>,

  /** Calendar glyph — the chip's mark from 390 up. */
  dateGlyph: {
    width: {
      xs: phone.dateChip.iconSize,
      sm: tablet.toolbar.dateIconSize,
      md: toolbar.dateIconSize,
    },
    height: {
      xs: phone.dateChip.iconSize,
      sm: tablet.toolbar.dateIconSize,
      md: toolbar.dateIconSize,
    },
    color: toolbar.searchIconColor,
    mr: {
      xs: phone.dateChip.gap,
      sm: tablet.toolbar.dateGap,
      md: toolbar.dateGap,
    },
    flexShrink: 0,
    [PHONE_SM_QUERY]: { display: "none" },
  } as SxProps<Theme>,

  /** Filter-lines glyph — the 375 column's icon-only stand-in for the chip. */
  dateGlyphCompact: {
    display: "none",
    width: phone.dateChip.squareIconSize,
    height: phone.dateChip.squareIconSize,
    color: toolbar.dateColor,
    flexShrink: 0,
    [PHONE_SM_QUERY]: { display: "block" },
  } as SxProps<Theme>,

  /** Both phone columns drop the caret; the chip is not drawn as a dropdown. */
  dateCaret: {
    display: { xs: "none", sm: "block" },
    width: toolbar.dateCaretSize,
    height: toolbar.dateCaretSize,
    color: toolbar.searchIconColor,
    ml: { sm: tablet.toolbar.dateGap, md: toolbar.dateGap },
    flexShrink: 0,
  } as SxProps<Theme>,

  /**
   * The phone reference runs the pills as one horizontally-scrolling line that
   * bleeds into the page gutters, so the first chip still lines up with the
   * cards while the rest scroll past the edge. The tablet keeps the desktop's
   * wrapping row.
   */
  /**
   * Wrapper the scroll hint is positioned against. The rail used to be
   * full-bleed — negative margins pulling it into the page gutter — so it sat
   * wider than every other block on the page and its last pill ran under the
   * viewport edge. It now keeps the column's own width.
   */
  tabsRail: {
    position: "relative",
    minWidth: 0,
    mb: { xs: 0, sm: 0, md: tabs.marginBottom },
  } as SxProps<Theme>,

  tabs: {
    flexWrap: { xs: "nowrap", sm: "wrap" },
    overflowX: { xs: "auto", sm: "visible" },
    gap: { xs: phone.tabs.gap, sm: tabs.gap },
  } as SxProps<Theme>,

  tab: (active: boolean) =>
    ({
      display: "inline-flex",
      alignItems: "center",
      gap: tabs.labelGap,
      minWidth: 0,
      // Nothing may shrink inside the phone's scrolling strip.
      flexShrink: 0,
      height: "auto",
      p: { xs: phone.tabs.padding.std, sm: tabs.padding },
      borderRadius: tabs.radius,
      border: `1px solid ${active ? tabs.activeBorder : tabs.border}`,
      bgcolor: active ? tabs.activeBg : tabs.bg,
      color: active ? tabs.activeColor : tabs.color,
      fontSize: { xs: phone.tabs.fontSize, sm: tabs.fontSize },
      fontWeight: tabs.fontWeight,
      lineHeight: "normal",
      textTransform: "none",
      whiteSpace: "nowrap",
      boxShadow: "none",
      "&:hover": {
        bgcolor: active ? tabs.activeBg : tabs.bg,
        borderColor: active ? tabs.activeBorder : tabs.border,
        boxShadow: "none",
      },
      "&.Mui-disabled": {
        bgcolor: active ? tabs.activeBg : tabs.bg,
        color: active ? tabs.activeColor : tabs.color,
        opacity: 0.6,
      },
      [PHONE_SM_QUERY]: { padding: phone.tabs.padding.sm },
    }) as SxProps<Theme>,

  tabCount: (active: boolean) =>
    ({
      minWidth: tabs.countMinWidth,
      p: tabs.countPadding,
      borderRadius: tabs.countRadius,
      bgcolor: active ? tabs.countActiveBg : tabs.countBg,
      color: active ? tabs.countActiveColor : tabs.countColor,
      fontSize: tabs.countFontSize,
      fontWeight: tabs.countFontWeight,
      lineHeight: "normal",
      textAlign: "center",
    }) as SxProps<Theme>,

  /**
   * On a phone the reference gives every folder and request its own card, so
   * the wrapper loses its chrome and becomes a plain stack; from `sm` up it is
   * the single white card the desktop screen draws.
   */
  listCard: {
    display: { xs: "flex", sm: "block" },
    flexDirection: "column",
    gap: { xs: phone.list.gap, sm: 0 },
    bgcolor: { xs: "transparent", sm: list.bg },
    border: { xs: "none", sm: `1px solid ${list.border}` },
    borderRadius: { xs: 0, sm: list.radius },
    boxShadow: { xs: "none", sm: list.shadow },
    overflow: { xs: "visible", sm: "hidden" },
    position: "relative",
    // The shared list skeleton draws bare rows; on a phone they have to read as
    // the cards the real rows become. Scoped to a raw `down(sm)` query rather
    // than a breakpoint value so the desktop skeleton's own hairline — a less
    // specific selector than this descendant one — is left alone.
    [PHONE_QUERY]: {
      "& > .MuiStack-root": {
        backgroundColor: list.bg,
        border: `1px solid ${list.border}`,
        borderRadius: phone.requestRow.radius,
      },
    },
  } as SxProps<Theme>,

  /**
   * A flex card on a phone; from `sm` up it joins the table grid. Folders only
   * ever come from the folder endpoint, so this is always the tag-less grid.
   */
  folderRow: {
    ...rowChrome,
    ...tableColumns(false),
    display: { xs: "flex", sm: "grid" },
    position: "relative",
    gap: { xs: phone.folderRow.gap, sm: table.gap },
    p: { xs: phone.folderRow.padding, sm: table.rowPadding },
    borderRadius: { xs: phone.folderRow.radius, sm: 0 },
    cursor: "pointer",
    "&:hover": { bgcolor: { xs: list.bg, sm: row.hoverBg } },
    [`&:hover .${TRAILING_CHEVRON_CLASS}`]: { opacity: 0 },
    [`&:hover .${TRAILING_ACTION_CLASS}`]: {
      opacity: 1,
      pointerEvents: "auto",
    },
  } as SxProps<Theme>,

  /**
   * A grid at every width. From `sm` up its cells fall into the table columns
   * in source order — a cell a width has no room for is `display: none`, which
   * takes it out of auto-placement. Below `sm` the reference stacks the card
   * into three lines, so the cells are placed by named area instead.
   */
  requestRow: (withTags: boolean) =>
    ({
      ...rowChrome,
      ...tableColumns(withTags),
      display: "grid",
      gridTemplateColumns: {
        xs: "minmax(0,1fr) auto",
        ...tableColumns(withTags).gridTemplateColumns,
      },
      gridTemplateAreas: {
        xs: `"lead actions" "status signers" "progress progress"`,
        sm: "none",
      },
      gap: { xs: phone.requestRow.blockGap, sm: table.gap },
      p: { xs: phone.requestRow.padding, sm: table.rowPadding },
      borderRadius: { xs: phone.requestRow.radius, sm: 0 },
      cursor: "pointer",
      "&:hover": { bgcolor: { xs: list.bg, sm: row.hoverBg } },
      [`&:hover .${TAG_ACTION_CLASS}, &:focus-within .${TAG_ACTION_CLASS}`]: {
        opacity: 1,
      },
    }) as SxProps<Theme>,

  /** Icon tile + text block, the row grid's first column. */
  rowLead: {
    gridArea: { xs: "lead", sm: "auto" },
    display: "flex",
    alignItems: { xs: "flex-start", sm: "center" },
    gap: { xs: phone.requestRow.headGap.std, sm: table.leadGap },
    minWidth: 0,
    [PHONE_SM_QUERY]: { gap: phone.requestRow.headGap.sm },
  } as SxProps<Theme>,

  /** Spans every column but the last, which holds the chevron. */
  folderLead: {
    gridColumn: { sm: "1 / -2" },
    display: "flex",
    alignItems: "center",
    gap: { xs: phone.folderRow.gap, sm: table.leadGap },
    flex: { xs: 1, sm: "unset" },
    minWidth: 0,
  } as SxProps<Theme>,

  iconTile: (bg: string) =>
    ({
      width: {
        xs: phone.requestRow.iconTileSize.std,
        sm: table.iconTileSize,
      },
      height: {
        xs: phone.requestRow.iconTileSize.std,
        sm: table.iconTileSize,
      },
      borderRadius: {
        xs: phone.requestRow.iconTileRadius,
        sm: table.iconTileRadius,
      },
      bgcolor: bg,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      [PHONE_SM_QUERY]: {
        width: phone.requestRow.iconTileSize.sm,
        height: phone.requestRow.iconTileSize.sm,
      },
    }) as SxProps<Theme>,

  folderIconTile: {
    width: { xs: phone.folderRow.iconTileSize, sm: table.iconTileSize },
    height: { xs: phone.folderRow.iconTileSize, sm: table.iconTileSize },
    borderRadius: {
      xs: phone.folderRow.iconTileRadius,
      sm: table.iconTileRadius,
    },
    bgcolor: row.folderIconBg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  } as SxProps<Theme>,

  requestGlyph: (color: string) =>
    ({
      width: { xs: phone.requestRow.iconSize.std, sm: table.iconSize },
      height: { xs: phone.requestRow.iconSize.std, sm: table.iconSize },
      color,
      [PHONE_SM_QUERY]: {
        width: phone.requestRow.iconSize.sm,
        height: phone.requestRow.iconSize.sm,
      },
    }) as SxProps<Theme>,

  folderGlyph: {
    width: { xs: phone.folderRow.iconSize, sm: row.folderIconSize },
    height: { xs: phone.folderRow.iconSize, sm: row.folderIconSize },
    color: row.folderIconStroke,
  } as SxProps<Theme>,

  /** Title block; must be able to shrink beside the tile and the kebab. */
  rowText: {
    flex: 1,
    minWidth: 0,
  } as SxProps<Theme>,

  rowTitle: (muted: boolean) =>
    ({
      fontSize: {
        xs: phone.requestRow.titleFontSize.std,
        sm: table.titleFontSize,
      },
      fontWeight: row.titleFontWeight,
      lineHeight: 1.35,
      color: muted ? row.titleMutedColor : row.titleColor,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      [PHONE_SM_QUERY]: { fontSize: phone.requestRow.titleFontSize.sm },
    }) as SxProps<Theme>,

  folderTitle: {
    fontSize: {
      xs: phone.folderRow.titleFontSize.std,
      sm: table.titleFontSize,
    },
    fontWeight: row.titleFontWeight,
    lineHeight: 1.35,
    color: row.titleColor,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    [PHONE_SM_QUERY]: { fontSize: phone.folderRow.titleFontSize.sm },
  } as SxProps<Theme>,

  folderSub: {
    mt: row.folderSubMarginTop,
    fontSize: { xs: phone.folderRow.subFontSize, sm: table.subFontSize },
    color: row.subColor,
    lineHeight: 1.35,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  } as SxProps<Theme>,

  /**
   * "Created by … · date". The widest grid gives the sender and the created
   * date columns of their own, so the line is dropped there.
   */
  requestSub: {
    display: { xs: "block", xl: "none" },
    mt: row.requestSubMarginTop,
    fontSize: { xs: phone.requestRow.subFontSize.std, sm: table.subFontSize },
    color: row.subColor,
    lineHeight: 1.35,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    [PHONE_SM_QUERY]: { fontSize: phone.requestRow.subFontSize.sm },
  } as SxProps<Theme>,

  /**
   * Folder rows draw a chevron at rest (as designed) and reveal the overflow
   * trigger in its place on hover, so folder actions stay reachable without
   * adding a control the design doesn't have.
   */
  trailingColumn: {
    gridColumn: { sm: "-2 / -1" },
    justifySelf: { sm: "end" },
    width: kebab.size,
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: kebab.size,
    flexShrink: 0,
  } as SxProps<Theme>,

  trailingChevron: (menuOpen: boolean) =>
    ({
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: row.chevronColor,
      opacity: menuOpen ? 0 : 1,
    }) as SxProps<Theme>,

  trailingAction: (menuOpen: boolean) =>
    ({
      position: "absolute",
      inset: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      opacity: menuOpen ? 1 : 0,
      pointerEvents: menuOpen ? "auto" : "none",
      "&:focus-within": { opacity: 1, pointerEvents: "auto" },
    }) as SxProps<Theme>,

  rowOpenButton: {
    position: "absolute",
    inset: 0,
    m: 0,
    p: 0,
    border: 0,
    background: "transparent",
    pointerEvents: "none",
    cursor: "inherit",
    outline: "none",
    "&:focus-visible": {
      outline: "2px solid",
      outlineColor: "primary.main",
      outlineOffset: "-2px",
    },
  } as SxProps<Theme>,

  /**
   * Avatars sit right of the status chip on a phone. The tablet grid has no
   * signers column, so the stack comes back at `lg`.
   */
  signersColumn: {
    gridArea: { xs: "signers", sm: "auto" },
    justifySelf: { xs: "end", sm: "start" },
    display: { xs: "flex", sm: "none", lg: "flex" },
    alignItems: "center",
    minWidth: 0,
  } as SxProps<Theme>,

  avatarStack: {
    display: "flex",
    alignItems: "center",
  } as SxProps<Theme>,

  /** The 2px ring sits inside the circle, as in the design. */
  avatar: (bg: string, isFirst: boolean) =>
    ({
      width: { xs: phone.avatars.size.std, sm: table.avatarSize },
      height: { xs: phone.avatars.size.std, sm: table.avatarSize },
      ml: isFirst ? 0 : { xs: phone.avatars.overlap, sm: avatars.overlap },
      border: `${avatars.ringWidth} solid ${avatars.ring}`,
      boxSizing: "border-box",
      bgcolor: bg,
      color: avatars.color,
      fontSize: { xs: phone.avatars.fontSize.std, sm: table.avatarFontSize },
      fontWeight: avatars.fontWeight,
      flexShrink: 0,
      [PHONE_SM_QUERY]: {
        width: phone.avatars.size.sm,
        height: phone.avatars.size.sm,
        fontSize: phone.avatars.fontSize.sm,
      },
    }) as SxProps<Theme>,

  /**
   * The avatar tooltip runs to two lines for an in-person signer (who, then how
   * they sign), so the newline in `getSignerTooltip` has to survive.
   */
  avatarTooltip: {
    whiteSpace: "pre-line",
    textAlign: "center",
    lineHeight: 1.45,
  } as SxProps<Theme>,

  avatarMore: {
    width: { xs: phone.avatars.size.std, sm: table.avatarSize },
    height: { xs: phone.avatars.size.std, sm: table.avatarSize },
    ml: { xs: phone.avatars.overlap, sm: avatars.overlap },
    border: `${avatars.ringWidth} solid ${avatars.ring}`,
    boxSizing: "border-box",
    bgcolor: avatars.moreBg,
    color: avatars.moreColor,
    fontSize: { xs: phone.avatars.fontSize.std, sm: table.avatarFontSize },
    fontWeight: avatars.fontWeight,
    flexShrink: 0,
    [PHONE_SM_QUERY]: {
      width: phone.avatars.size.sm,
      height: phone.avatars.size.sm,
      fontSize: phone.avatars.fontSize.sm,
    },
  } as SxProps<Theme>,

  avatarsEmpty: {
    fontSize: {
      xs: phone.avatars.emptyFontSize.std,
      sm: avatars.emptyFontSize,
    },
    color: avatars.emptyColor,
    fontStyle: "italic",
    lineHeight: "normal",
    whiteSpace: "nowrap",
    [PHONE_SM_QUERY]: { fontSize: phone.avatars.emptyFontSize.sm },
  } as SxProps<Theme>,

  /**
   * The status cell from `sm` up. Below it the wrapper goes transparent so the
   * chip and the progress bar become grid items of their own and can sit on
   * separate lines of the card.
   */
  statusColumn: {
    display: { xs: "contents", sm: "block" },
    minWidth: 0,
  } as SxProps<Theme>,

  statusChip: (key: SignRequestsStatusKey) =>
    ({
      gridArea: { xs: "status", sm: "auto" },
      justifySelf: "start",
      display: "inline-flex",
      alignItems: "center",
      gap: { xs: phone.status.chipGap, sm: status.chipGap },
      p: { xs: phone.status.chipPadding.std, sm: table.chipPadding },
      borderRadius: status.chipRadius,
      bgcolor: signRequestsStatusTokens[key].bg,
      maxWidth: "100%",
      flexShrink: 0,
      [PHONE_SM_QUERY]: { padding: phone.status.chipPadding.sm },
    }) as SxProps<Theme>,

  statusDot: (key: SignRequestsStatusKey) =>
    ({
      width: { xs: phone.status.dotSize, sm: status.dotSize },
      height: { xs: phone.status.dotSize, sm: status.dotSize },
      borderRadius: "50%",
      bgcolor: signRequestsStatusTokens[key].dot,
      flexShrink: 0,
    }) as SxProps<Theme>,

  statusLabel: (key: SignRequestsStatusKey) =>
    ({
      fontSize: {
        xs: phone.status.labelFontSize.std,
        sm: status.labelFontSize,
      },
      fontWeight: status.labelFontWeight,
      lineHeight: "normal",
      color: signRequestsStatusTokens[key].color,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      [PHONE_SM_QUERY]: { fontSize: phone.status.labelFontSize.sm },
    }) as SxProps<Theme>,

  /** The table layout has no progress bar; it stays on the phone card. */
  progressRow: {
    gridArea: { xs: "progress", sm: "auto" },
    display: { xs: "flex", sm: "none" },
    alignItems: "center",
    gap: phone.status.progressGap,
  } as SxProps<Theme>,

  progressTrack: {
    flex: 1,
    minWidth: 0,
    height: status.barHeight,
    borderRadius: status.barRadius,
    bgcolor: status.barTrack,
    overflow: "hidden",
  } as SxProps<Theme>,

  progressFill: (key: SignRequestsStatusKey, pct: number) =>
    ({
      height: "100%",
      width: `${pct}%`,
      borderRadius: status.barRadius,
      bgcolor: signRequestsStatusTokens[key].dot,
    }) as SxProps<Theme>,

  progressNote: {
    fontSize: { xs: phone.status.noteFontSize.std, sm: status.noteFontSize },
    fontWeight: status.noteFontWeight,
    color: status.noteColor,
    lineHeight: "normal",
    whiteSpace: "nowrap",
    [PHONE_SM_QUERY]: { fontSize: phone.status.noteFontSize.sm },
  } as SxProps<Theme>,

  /**
   * The row's last cell: the tag trigger, then the overflow menu. Pulled into
   * the card's top-right corner on a phone.
   */
  actionsColumn: {
    gridArea: { xs: "actions", sm: "auto" },
    alignSelf: { xs: "start", sm: "center" },
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: table.actionsGap,
    m: { xs: phone.requestRow.kebabMargin, sm: 0 },
  } as SxProps<Theme>,

  /**
   * Wraps the tag trigger: hidden until the row is hovered or holds focus, and
   * held open while its picker is. A phone has no hover, so it is left out.
   */
  tagAction: (open: boolean) =>
    ({
      display: { xs: "none", sm: "flex" },
      opacity: open ? 1 : 0,
    }) as SxProps<Theme>,

  kebabButton: {
    width: { xs: phone.requestRow.kebabSize, sm: kebab.size },
    height: { xs: phone.requestRow.kebabSize, sm: kebab.size },
    p: 0,
    borderRadius: kebab.radius,
    color: kebab.color,
    "&:hover": { bgcolor: kebab.hoverBg, color: kebab.hoverColor },
  } as SxProps<Theme>,

  kebabGlyph: {
    width: { xs: phone.requestRow.kebabIconSize, sm: kebab.iconSize },
    height: { xs: phone.requestRow.kebabIconSize, sm: kebab.iconSize },
  } as SxProps<Theme>,

  emptyBlock: {
    p: empty.padding,
    textAlign: "center",
  } as SxProps<Theme>,

  emptyTitle: {
    fontSize: empty.titleFontSize,
    fontWeight: empty.titleFontWeight,
    color: empty.titleColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  emptySub: {
    mt: empty.subMarginTop,
    fontSize: empty.subFontSize,
    color: empty.subColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  /**
   * "Nothing matches" inside the list. On a phone the list card is gone, so
   * this block has to carry the card chrome itself.
   */
  listEmptyBlock: {
    p: { xs: phone.empty.padding.std, md: empty.padding },
    textAlign: "center",
    bgcolor: { xs: list.bg, sm: "transparent" },
    border: { xs: `1px solid ${list.border}`, sm: "none" },
    borderRadius: { xs: phone.empty.radius, sm: 0 },
    [PHONE_SM_QUERY]: { padding: phone.empty.padding.sm },
  } as SxProps<Theme>,

  listEmptyTitle: {
    fontSize: { xs: phone.empty.titleFontSize.std, md: empty.titleFontSize },
    fontWeight: { xs: phone.empty.titleFontWeight, md: empty.titleFontWeight },
    color: empty.titleColor,
    lineHeight: "normal",
    [PHONE_SM_QUERY]: { fontSize: phone.empty.titleFontSize.sm },
  } as SxProps<Theme>,

  listEmptySub: {
    mt: empty.subMarginTop,
    fontSize: { xs: phone.empty.subFontSize.std, md: empty.subFontSize },
    color: empty.subColor,
    lineHeight: "normal",
    [PHONE_SM_QUERY]: { fontSize: phone.empty.subFontSize.sm },
  } as SxProps<Theme>,

  freshCard: {
    bgcolor: list.bg,
    border: `1px solid ${freshState.border}`,
    borderRadius: { xs: phone.freshState.radius, md: freshState.radius },
    boxShadow: freshState.shadow,
    p: {
      xs: phone.freshState.padding.std,
      sm: tablet.freshState.padding,
      md: freshState.padding,
    },
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    [PHONE_SM_QUERY]: { padding: phone.freshState.padding.sm },
  } as SxProps<Theme>,

  freshIcon: {
    width: { xs: phone.freshState.iconSize.std, sm: freshState.iconSize },
    height: { xs: phone.freshState.iconSize.std, sm: freshState.iconSize },
    borderRadius: "50%",
    bgcolor: freshState.iconBg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    mb: {
      xs: phone.freshState.iconMarginBottom.std,
      sm: freshState.iconMarginBottom,
    },
    [PHONE_SM_QUERY]: {
      width: phone.freshState.iconSize.sm,
      height: phone.freshState.iconSize.sm,
      marginBottom: phone.freshState.iconMarginBottom.sm,
    },
  } as SxProps<Theme>,

  freshTitle: {
    fontSize: {
      xs: phone.freshState.titleFontSize.std,
      sm: freshState.titleFontSize,
    },
    fontWeight: freshState.titleFontWeight,
    color: freshState.titleColor,
    lineHeight: "normal",
    textAlign: "center",
    [PHONE_SM_QUERY]: { fontSize: phone.freshState.titleFontSize.sm },
  } as SxProps<Theme>,

  freshBody: {
    mt: freshState.bodyMarginTop,
    maxWidth: { xs: "none", sm: freshState.bodyMaxWidth },
    fontSize: {
      xs: phone.freshState.bodyFontSize.std,
      sm: freshState.bodyFontSize,
    },
    color: freshState.bodyColor,
    lineHeight: {
      xs: phone.freshState.bodyLineHeight,
      sm: freshState.bodyLineHeight,
    },
    textAlign: "center",
    [PHONE_SM_QUERY]: { fontSize: phone.freshState.bodyFontSize.sm },
  } as SxProps<Theme>,

  freshActions: {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    alignItems: "center",
    gap: freshState.actionsGap,
    width: { xs: "100%", sm: "auto" },
    mt: {
      xs: phone.freshState.actionsMarginTop.std,
      sm: freshState.actionsMarginTop,
    },
    [PHONE_SM_QUERY]: { marginTop: phone.freshState.actionsMarginTop.sm },
  } as SxProps<Theme>,
};

export default signRequestsStyles;
